import { Button } from '@/components/Button';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/Dialog';
import Text from '@/components/Text';
import { cn } from '@/lib/cn';
import { Description } from '@radix-ui/react-dialog';
import {
    emptyGroupingSpec,
    emptySortSpec,
    getFilterSpec,
    getGroupingSpec,
    getSortSpec,
    type DataStreamBaseTileConfig
} from '@squaredup/data-streams';
import { ConfigId } from '@squaredup/ids';
import type { ResolvedUIConfig } from 'components/forms/jsonForms/resolveAutocompleteOptions';
import LoadingSpinner from 'components/LoadingSpinner';
import { ModalButtons } from 'components/Modal';
import { useScopeContentToObjects } from 'queries/hooks/useScopeContentToObjects';
import { useState, type FC } from 'react';
import { FormProvider } from 'react-hook-form';
import type { DataStream } from 'services/DataStreamDefinitionService';
import { SaveAsDataStreamForm } from './SaveAsDataStreamForm';
import { UIPreview } from './UIPreview';
import {
    getSupportedTimeframes,
    saveAsDataStreamFormToDataStreamDefinition,
    useSaveAsDataStreamForm,
    useSaveAsDataStreamMutation,
    type DataStreamSchemaFormData
} from './useSaveAsDataStream';

interface SaveAsDataStreamModalProps {
    config: DataStreamBaseTileConfig;
    baseDataStream: DataStream;
    basePluginConfigId: ConfigId['value'];
    dataSourceConfig: Record<string, unknown>;
    onSaveDataStream: (savedDataStream: DataStream) => void;
    onClose?: () => void;
}

export const SaveAsDataStreamModal: FC<SaveAsDataStreamModalProps> = ({
    baseDataStream,
    basePluginConfigId,
    config,
    dataSourceConfig,
    children,
    onSaveDataStream,
    onClose
}) => {
    const [isOpen, setIsOpenImpl] = useState(false);

    const availableTimeframes = getSupportedTimeframes(baseDataStream.definition).filter((t) => t !== 'none');

    const hasShapingConfig =
        getGroupingSpec(config.dataStream) !== emptyGroupingSpec ||
        getSortSpec(config.dataStream) !== emptySortSpec ||
        getFilterSpec(config.dataStream) !== undefined;

    const { form } = useSaveAsDataStreamForm({
        defaultValues: {
            displayName: '',
            restrictTimeframes: Boolean(config.timeframe),
            includeMetadata: Boolean(config.dataStream?.metadata),
            includeShaping: hasShapingConfig,
            allowedTimeframes: availableTimeframes,
            includeUI: false,
            uiConfig: [],
            uiConfigMapping: { ...baseDataStream.definition.dataSourceConfig, ...dataSourceConfig }
        }
    });

    const setIsOpen = (newIsOpen: boolean) => {
        if (!newIsOpen) {
            onClose?.();
        }

        setIsOpenImpl(newIsOpen);
    };

    const { mutateAsync: saveAsDataStream } = useSaveAsDataStreamMutation({
        onSuccess: (savedDataStream) => {
            onSaveDataStream(savedDataStream);
            setIsOpen(false);
        }
    });

    const { data: singleObjectScopeNode = [] } = useScopeContentToObjects(config.scope);

    const saveFormData = (formData: DataStreamSchemaFormData) => {
        return saveAsDataStream({
            displayName: formData.displayName ?? '',
            dataSourceName: baseDataStream.dataSourceName,
            definition: {
                ...saveAsDataStreamFormToDataStreamDefinition(
                    baseDataStream,
                    config.dataStream,
                    formData,
                    singleObjectScopeNode[0]?.sourceId?.[0] as ConfigId['value']
                ),
                presetOf: undefined,
                dataSourceConfig
            },
            template: formData.includeUI ? formData.uiConfig : undefined,
            sourceTemplateId: undefined,
            pluginId: baseDataStream.pluginId,
            parentPluginId: new ConfigId(basePluginConfigId),
            parentPluginVersion: ''
        }).catch((e) => {
            // eslint-disable-next-line no-console
            console.error('Error saving data stream:', e);
        });
    };

    const isUiEnabled = form.watch('includeUI');
    const isSubmitting = form.formState.isSubmitting;

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger>{children}</DialogTrigger>

            <DialogContent
                id='saveAsDataStreamModal'
                className={cn(
                    'fixed z-50 h-fit max-h-[calc(100vh-8rem)] flex flex-row m-auto bg-backgroundSecondary border-2 border-modalOutline shadow-lg inset-0 text-textPrimary',
                    isUiEnabled && 'max-w-[1280px]',
                    !isUiEnabled && 'max-w-4xl'
                )}
            >
                <Description className='hidden'>Save the current tile as a data stream.</Description>

                <div className='flex flex-col w-full grow basis-[67%] scroll-overflow'>
                    <div className='flex-shrink-0 px-8 my-4 overflow-hidden'>
                        <DialogTitle asChild>
                            <Text.H2 className='truncate text-textPrimary'>Save as data stream</Text.H2>
                        </DialogTitle>
                    </div>

                    <FormProvider {...form}>
                        <form
                            onSubmit={form.handleSubmit(saveFormData)}
                            className={cn('flex flex-col min-h-0 min-w-0')}
                            aria-label='Save as data stream form'
                        >
                            <SaveAsDataStreamForm
                                form={form}
                                availableTimeframes={availableTimeframes}
                                allowSavingCustomMetadata={config.dataStream?.metadata != null}
                                allowSavingShaping={hasShapingConfig}
                            />

                            <ModalButtons>
                                <Button
                                    disabled={isSubmitting}
                                    variant='secondary'
                                    type='button'
                                    onClick={() => setIsOpen(false)}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    disabled={isSubmitting || !form.formState.isValid}
                                    type='submit'
                                    {...(isSubmitting && { icon: <LoadingSpinner size={18} /> })}
                                >
                                    Save
                                </Button>
                            </ModalButtons>
                        </form>
                    </FormProvider>
                </div>

                {isUiEnabled && (
                    <div className='grow basis-[33%]'>
                        <UIPreview
                            datasourceConfig={dataSourceConfig}
                            uiConfig={form.watch('uiConfig') as ResolvedUIConfig[]}
                            uiConfigMapping={form.watch('uiConfigMapping')}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
